import { Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

/**
 * The label style shared by every field and section heading in a detail card.
 * Exported because a section heading outside the grid has to match its fields
 * exactly — two copies of these classes drift the moment one is adjusted.
 */
export const DETAIL_LABEL_CLASS =
  'text-[11px] leading-tight font-semibold tracking-wide uppercase';

interface DetailFieldProps {
  label: string;
  /** Rendered as an em dash when empty — a blank row reads as a broken page. */
  value?: string | null;
  /** Replaces `value` when the field needs more than text (a chip, a link). */
  children?: ReactNode;
  className?: string;
}

/**
 * A label above its value.
 *
 * Label-above rather than label-beside: at 360px a side-by-side pair either
 * truncates the value or wraps into a shape that no longer reads as one field.
 * The value uses `break-words` because an email address is a single long token
 * and would otherwise push the card into horizontal scroll.
 */
export const DetailField = ({ label, value, children, className }: DetailFieldProps) => (
  <div className={cn('min-w-0', className)}>
    <Typography
      variant="caption"
      component="dt"
      color="text.secondary"
      className={DETAIL_LABEL_CLASS}
    >
      {label}
    </Typography>
    <dd className="mt-1 min-w-0">
      {children ?? (
        <Typography className="text-[15px] leading-snug break-words">
          {value !== null && value !== undefined && value !== '' ? value : '—'}
        </Typography>
      )}
    </dd>
  </div>
);

interface DetailFieldGridProps {
  children: ReactNode;
  className?: string;
}

/** One column on a phone, two from `sm`. Built base-first: columns are ADDED
 *  at the breakpoint, never removed below it. */
export const DetailFieldGrid = ({ children, className }: DetailFieldGridProps) => (
  <dl className={cn('grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2', className)}>
    {children}
  </dl>
);
